import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CompanyLogo } from "@/components/ui/company-logo";
import { MapPin, Clock, DollarSign, Star, Target, Eye, Zap, Home, AlertTriangle } from "lucide-react";
import { JobApplication } from "@/components/JobApplication";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import { Link } from "react-router-dom";

interface JobCardProps {
  job: {
    id: string;
    title: string;
    company: {
      name: string;
      logo_url?: string;
      is_leading_workplace_certified?: boolean;
    };
    location: string;
    job_type: string;
    experience_level: string;
    salary_min?: number;
    salary_max?: number;
    currency: string;
    is_remote: boolean;
    featured: boolean;
    urgent?: boolean;
    created_at: string;
    matchScore?: number;
  };
  onApply?: (jobId: string) => void;
  showApplyButton?: boolean;
}

export const JobCard = ({ job, onApply, showApplyButton = true }: JobCardProps) => {
  const { language } = useLanguage();
  const t = useTranslations(language);
  
  const formatSalary = (min?: number, max?: number, currency = 'LKR') => {
    if (!min && !max) return null;
    if (min && max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    return `${currency} ${(min || max)?.toLocaleString()}`;
  };

  const getTimeSince = (date: string) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now.getTime() - posted.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ${t.jobCard.ago}`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ${t.jobCard.ago}`;
  };

  return (
    <Card className={`job-card shadow-card hover:shadow-primary transition-all duration-300 hover:-translate-y-1 ${
      job.featured ? 'ring-2 ring-primary shadow-primary border-primary/20' : ''
    }`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <CompanyLogo 
              logoUrl={job.company.logo_url}
              companyName={job.company.name}
              size="md"
            />
            <div className="flex-1">
              <CardTitle className="text-lg leading-tight mb-1 hover:text-primary transition-colors">
                {job.title}
              </CardTitle>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm font-medium">{job.company.name}</p>
                {job.company.is_leading_workplace_certified && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs px-1.5 py-0.5">
                    Leading Workplace
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {job.matchScore && (
              <Badge 
                variant="secondary" 
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold"
              >
                <Target className="w-3 h-3 mr-1" />
                {job.matchScore}% Match
              </Badge>
            )}
            {job.urgent && (
              <Badge className="bg-red-500 text-white animate-pulse">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Urgent
              </Badge>
            )}
            {job.featured && (
              <Badge variant="default" className="bg-primary text-white">
                <Star className="w-3 h-3 mr-1" />
                {language === 'zh-CN' ? '精选' : 'Featured'}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="py-3">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">
              <MapPin className="w-3 h-3 mr-1" />
              {job.location}
            </Badge>
            {job.is_remote && (
              <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                <Home className="w-3 h-3 mr-1" />
                {t.jobCard.remote}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {job.job_type}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {job.experience_level}
            </Badge>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
              <Zap className="w-3 h-3 mr-1" />
              Quick Apply
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            {formatSalary(job.salary_min, job.salary_max, job.currency) && (
              <div className="flex items-center text-salary-highlight font-semibold">
                <DollarSign className="w-4 h-4 mr-1" />
                {formatSalary(job.salary_min, job.salary_max, job.currency)}
              </div>
            )}
            <div className="flex items-center text-muted-foreground">
              <Clock className="w-4 h-4 mr-1" />
              {getTimeSince(job.created_at)}
            </div>
          </div>
        </div>
      </CardContent>
      
      {showApplyButton && (
        <CardFooter className="pt-3">
          <div className="flex gap-2 w-full">
            <Button variant="outline" size="sm" asChild className="flex-1">
              <Link to={`/jobs/${job.id}`}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Link>
            </Button>
            {onApply ? (
              <Button 
                onClick={() => onApply(job.id)}
                className="flex-1 gradient-primary hover:opacity-90 transition-opacity"
                size="sm"
              >
                {t.jobCard.applyNow}
              </Button>
            ) : (
              <div className="flex-1">
                <JobApplication 
                  jobId={job.id} 
                  jobTitle={job.title}
                />
              </div>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};